import { useEffect, useRef } from "react";

/**
 * Sistema cinético del hero.
 *
 * No es un campo de partículas ni una textura decorativa: representa el recorrido del producto
 * como una máquina editorial viva. Tres anillos (entender, practicar, crear) orbitan un núcleo IA
 * y una señal roja atraviesa la grilla hacia la experiencia de aprendizaje. Todo es SVG/CSS:
 * determinístico, liviano, nítido en cualquier DPR y legible aun sin WebGL.
 */
export function HeroKineticField() {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let frame = 0;
    const onPointerMove = (event: PointerEvent) => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const x = event.clientX / window.innerWidth - 0.5;
        const y = event.clientY / window.innerHeight - 0.5;
        root.style.setProperty("--hero-field-x", x.toFixed(3));
        root.style.setProperty("--hero-field-y", y.toFixed(3));
      });
    };

    window.addEventListener("pointermove", onPointerMove, { passive: true });
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("pointermove", onPointerMove);
    };
  }, []);

  return (
    <div
      ref={rootRef}
      data-testid="hero-kinetic-field"
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 z-0 overflow-hidden bg-[#F5F0E4] dark:bg-[#181512]"
    >
      <style>{`
        @keyframes academia-core-clockwise {
          to { transform: rotate(360deg); }
        }
        @keyframes academia-core-counter {
          to { transform: rotate(-360deg); }
        }
        @keyframes academia-signal-run {
          to { stroke-dashoffset: -180; }
        }
        @keyframes academia-core-pulse {
          0%, 100% { transform: scale(.96); opacity: .68; }
          50% { transform: scale(1.04); opacity: 1; }
        }
        @keyframes academia-scan {
          0%, 12% { transform: translateX(-120%); opacity: 0; }
          22%, 66% { opacity: .8; }
          78%, 100% { transform: translateX(220%); opacity: 0; }
        }
        .academia-core-cw {
          transform-box: fill-box;
          transform-origin: center;
          animation: academia-core-clockwise 46s linear infinite;
        }
        .academia-core-ccw {
          transform-box: fill-box;
          transform-origin: center;
          animation: academia-core-counter 64s linear infinite;
        }
        .academia-signal-path {
          animation: academia-signal-run 8s linear infinite;
        }
        .academia-core-pulse {
          transform-box: fill-box;
          transform-origin: center;
          animation: academia-core-pulse 6s ease-in-out infinite;
        }
        .academia-core-scan {
          animation: academia-scan 9s cubic-bezier(.16,1,.3,1) infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .academia-core-cw,
          .academia-core-ccw,
          .academia-signal-path,
          .academia-core-pulse,
          .academia-core-scan { animation: none !important; }
        }
      `}</style>

      {/* Grilla editorial: estructura, no textura. Se desvanece antes de competir con el copy. */}
      <div
        className="absolute inset-0 opacity-[0.22] dark:opacity-[0.12]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(31,26,21,.11) 1px,transparent 1px),linear-gradient(90deg,rgba(31,26,21,.11) 1px,transparent 1px)",
          backgroundSize: "72px 72px",
          maskImage: "linear-gradient(90deg,transparent 0%,black 16%,black 88%,transparent 100%)",
        }}
      />

      {/* IA tipográfica: una sola masa grande y reconocible en vez de cientos de motas. */}
      <div
        className="font-display absolute -right-[3vw] top-[5vh] hidden select-none text-[clamp(310px,42vw,680px)] font-black leading-none tracking-[-0.13em] text-transparent opacity-[0.095] md:block dark:opacity-[0.12]"
        style={{
          WebkitTextStroke: "2px #C0392B",
          transform:
            "translate3d(calc(var(--hero-field-x, 0) * -18px), calc(var(--hero-field-y, 0) * -12px), 0)",
        }}
      >
        IA
      </div>

      {/* Núcleo orbital. En desktop abraza la ilustración; en mobile queda detrás del primer fold. */}
      <div
        className="absolute -right-[24rem] -top-[17rem] size-[760px] sm:-right-[20rem] sm:-top-[14rem] sm:size-[900px] md:-right-[13rem] md:top-[-8rem] md:size-[940px] lg:-right-[5rem] lg:top-[-6rem]"
        style={{
          transform:
            "translate3d(calc(var(--hero-field-x, 0) * 24px), calc(var(--hero-field-y, 0) * 17px), 0)",
        }}
      >
        <svg viewBox="0 0 1000 1000" className="size-full overflow-visible">
          <g fill="none">
            <circle cx="500" cy="500" r="430" stroke="#1F1A15" strokeOpacity=".10" />
            <circle
              cx="500"
              cy="500"
              r="370"
              stroke="#2B7D87"
              strokeOpacity=".34"
              strokeWidth="2"
              strokeDasharray="2 18 74 13"
              className="academia-core-cw"
            />
            <circle
              cx="500"
              cy="500"
              r="305"
              stroke="#C0392B"
              strokeOpacity=".46"
              strokeWidth="3"
              strokeDasharray="110 22 5 35"
              className="academia-core-ccw"
            />
            <circle
              cx="500"
              cy="500"
              r="238"
              stroke="#1F1A15"
              strokeOpacity=".18"
              strokeWidth="1.5"
              strokeDasharray="1 12"
              className="academia-core-cw"
            />
            <path
              d="M170 610C292 320 517 218 824 350"
              stroke="#C0392B"
              strokeOpacity=".62"
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray="10 18 72 22"
              className="academia-signal-path"
            />
            <path
              d="M208 690C366 472 602 386 864 478"
              stroke="#2B7D87"
              strokeOpacity=".34"
              strokeWidth="2"
              strokeDasharray="4 15"
              className="academia-signal-path"
            />
          </g>

          <g className="academia-core-pulse max-md:hidden">
            <circle cx="500" cy="500" r="92" fill="#C0392B" fillOpacity=".08" />
            <circle
              cx="500"
              cy="500"
              r="64"
              fill="#F5F0E4"
              stroke="#C0392B"
              strokeWidth="2"
              strokeOpacity=".7"
            />
            <text
              x="500"
              y="520"
              textAnchor="middle"
              fill="#C0392B"
              fontFamily="DM Sans, sans-serif"
              fontSize="51"
              fontWeight="800"
              letterSpacing="-3"
            >
              IA
            </text>
          </g>

          {/* Acá vivían tres "estaciones" (01 ENTENDER / 02 PRACTICAR / 03 CREAR).
              Se sacaron: eran una secuencia numerada que no se podía ni leer ni seguir.
              Medido sobre la crema daban 2.62:1 —por debajo del mínimo 3.0 incluso para
              elementos no textuales— y estaban puestas en coordenadas que dejaban el 02
              ARRIBA del 01, así que de arriba a abajo se leía 02, 01, 03. Ornamento que
              no informa: fuera. Las curvas y las partículas de tinta se quedan, esas sí
              son atmósfera y no pretenden decir nada. */}
        </svg>
      </div>

      {/* Barrido de señal: una ruptura geométrica fuerte que recorre el sistema lentamente. */}
      <div className="academia-core-scan absolute -bottom-[20%] left-[38%] h-[150%] w-[11%] -skew-x-[18deg] border-x border-landing-red/20 bg-landing-red/[0.035]" />

      {/* Vignette central: protege el titular sin convertir el fondo en otro degradé anaranjado. */}
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(245,240,228,0.98)_0%,rgba(245,240,228,0.88)_36%,rgba(245,240,228,0.24)_67%,rgba(245,240,228,0.04)_100%)] dark:bg-[linear-gradient(90deg,rgba(24,21,18,0.98)_0%,rgba(24,21,18,0.88)_36%,rgba(24,21,18,0.24)_67%,rgba(24,21,18,0.04)_100%)] max-md:bg-[linear-gradient(180deg,rgba(245,240,228,0.18)_0%,rgba(245,240,228,0.70)_42%,rgba(245,240,228,0.98)_62%)] max-md:dark:bg-[linear-gradient(180deg,rgba(24,21,18,0.18)_0%,rgba(24,21,18,0.70)_42%,rgba(24,21,18,0.98)_62%)]" />
    </div>
  );
}
